/**
 * O desenho da arte no canvas 2D.
 *
 * Este módulo não decide nada: ele executa o plano que `src/arte/layout.ts`
 * monta. Toda coordenada vem de lá, onde tem teste; aqui fica só o que exige
 * um contexto de canvas de verdade — gradiente, recorte de foto, medição de
 * texto.
 *
 * A MESMA função desenha o preview e a exportação, no mesmo tamanho. É isso
 * que faz "o que se vê é o que sai" ser verdade e não promessa: se o preview
 * fosse menor e reescalado, a quebra de linha e o recorte da foto poderiam
 * cair em lugares diferentes.
 */
import {
  ALTURA,
  COR,
  FONTE,
  LARGURA,
  MARGEM,
  SEGURO_BASE,
  SEGURO_TOPO,
  type Plano,
  type Primitiva,
  type Texto
} from "../../arte/layout.js";

/**
 * As imagens já carregadas.
 *
 * `A` e `B` são os slots de foto; `null` desenha o placeholder. `logo` é a
 * marca, quando a arte usa um PNG da biblioteca — ausente enquanto carrega, e
 * aí simplesmente não se desenha nada. Placeholder de logo seria pior: ele
 * entraria no arquivo exportado se alguém gerasse durante o carregamento.
 */
export interface Imagens {
  A?: HTMLImageElement | null;
  B?: HTMLImageElement | null;
  logo?: HTMLImageElement | null;
}

/** @deprecated nome antigo de `Imagens`, de quando só havia fotos. */
export type Fotos = Imagens;

function fonteCss(t: Pick<Texto, "peso" | "tamanho" | "fonte">): string {
  return `${t.peso} ${t.tamanho}px ${FONTE[t.fonte]}`;
}

/**
 * Largura de um texto com tracking.
 *
 * `measureText` não conhece o espaçamento extra, então ele entra na conta à
 * mão — n-1 espaços entre n caracteres, nunca n, senão o alinhamento à
 * direita sai deslocado por um tracking inteiro.
 */
function larguraCom(
  ctx: CanvasRenderingContext2D,
  texto: string,
  tracking: number
): number {
  const base = ctx.measureText(texto).width;
  if (tracking === 0 || texto.length < 2) return base;
  return base + tracking * (texto.length - 1);
}

/**
 * Escreve uma linha aplicando tracking caractere a caractere.
 *
 * O `ctx.letterSpacing` resolveria isto em uma linha, mas ele não existe em
 * todos os navegadores e falha em silêncio onde não existe — o texto sai
 * apertado e ninguém vê. Caractere a caractere funciona em qualquer lugar.
 */
function escrever(
  ctx: CanvasRenderingContext2D,
  texto: string,
  x: number,
  y: number,
  tracking: number
): void {
  if (tracking === 0) {
    ctx.fillText(texto, x, y);
    return;
  }
  let cursor = x;
  for (const ch of texto) {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + tracking;
  }
}

/**
 * Uma linha do `manif` com a palavra em destaque pintada de lilás.
 *
 * Os três trechos são medidos com a MESMA fonte do título — é por isso que a
 * seção de inicialização espera as fontes carregarem antes do primeiro
 * render. Com a fonte de fallback, `measureText` devolve outra largura e o
 * trecho depois do destaque sai sobreposto.
 */
function escreverComDestaque(
  ctx: CanvasRenderingContext2D,
  linha: string,
  destaque: string,
  x: number,
  y: number
): void {
  const i = linha.indexOf(destaque);
  if (i < 0) {
    ctx.fillStyle = COR.texto;
    ctx.fillText(linha, x, y);
    return;
  }

  const antes = linha.slice(0, i);
  const depois = linha.slice(i + destaque.length);

  let cursor = x;
  for (const [trecho, cor] of [
    [antes, COR.texto],
    [destaque, COR.lilas],
    [depois, COR.texto]
  ] as const) {
    if (trecho === "") continue;
    ctx.fillStyle = cor;
    ctx.fillText(trecho, cursor, y);
    cursor += ctx.measureText(trecho).width;
  }
}

/**
 * Desenha a imagem preenchendo o retângulo, sem deformar.
 *
 * `cover`: escala pelo maior dos dois fatores, centraliza e recorta o que
 * sobra. Nunca estica e nunca deixa borda vazia — uma foto de campanha
 * esticada é pior que uma foto cortada.
 */
function cover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  const escala = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const dw = img.naturalWidth * escala;
  const dh = img.naturalHeight * escala;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
}

function placeholder(
  ctx: CanvasRenderingContext2D,
  rotulo: string,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  const g = ctx.createLinearGradient(x, y, x + w, y + h);
  g.addColorStop(0, "#3A2F6B");
  g.addColorStop(1, COR.card);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);

  ctx.save();
  ctx.font = `500 26px ${FONTE.mono}`;
  ctx.fillStyle = "rgba(244,244,248,0.35)";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(rotulo, x + w / 2, y + h / 2);
  ctx.restore();
}

/** O logo é vetor desenhado, não imagem — não depende de carregar nada. */
function desenharLogo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  escala: number
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(escala, escala);

  ctx.strokeStyle = COR.texto;
  ctx.lineWidth = 2.4;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(6, 26);
  ctx.lineTo(16, 6);
  ctx.lineTo(26, 26);
  ctx.stroke();

  ctx.strokeStyle = COR.roxo;
  ctx.lineWidth = 3.2;
  ctx.beginPath();
  ctx.moveTo(11, 19);
  ctx.lineTo(24, 19);
  ctx.stroke();
  ctx.restore();

  // A palavra fica FORA do transform: a escala do símbolo não deve esticar a
  // tipografia junto.
  ctx.save();
  ctx.font = `700 30px ${FONTE.sans}`;
  ctx.fillStyle = COR.texto;
  ctx.textBaseline = "alphabetic";
  ctx.fillText("AntiFila", x + 48.125, y + 29.75);
  ctx.restore();
}

/**
 * O logo da biblioteca, encaixado na caixa por `contain`.
 *
 * `contain` e não `cover`: a foto pode ser recortada porque o recorte é
 * decisão de composição, mas **logo recortado é marca adulterada**. Se não
 * couber, encolhe.
 *
 * Ancorado à esquerda e centrado na vertical, para que marcas de proporções
 * diferentes — uma assinatura larga, um selo quadrado — caiam todas na mesma
 * margem e no mesmo eixo óptico.
 */
function desenharLogoPng(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  if (img.naturalWidth === 0 || img.naturalHeight === 0) return;
  const escala = Math.min(w / img.naturalWidth, h / img.naturalHeight);
  const dw = img.naturalWidth * escala;
  const dh = img.naturalHeight * escala;
  ctx.drawImage(img, x, y + (h - dh) / 2, dw, dh);
}

function desenharTexto(ctx: CanvasRenderingContext2D, t: Texto): void {
  if (t.linhas.length === 0) return;

  ctx.save();
  ctx.font = fonteCss(t);
  ctx.fillStyle = t.cor;
  ctx.textBaseline = "alphabetic";
  // O alinhamento é resolvido à mão, e não por `ctx.textAlign`, porque com
  // tracking o navegador alinharia pela largura SEM o espaçamento extra.
  ctx.textAlign = "left";

  t.linhas.forEach((linha, i) => {
    const y = t.y + i * t.entrelinha;
    const x =
      t.alinhamento === "direita"
        ? t.x - larguraCom(ctx, linha, t.tracking)
        : t.x;

    if (t.destaque && linha.includes(t.destaque)) {
      escreverComDestaque(ctx, linha, t.destaque, x, y);
      ctx.fillStyle = t.cor;
    } else {
      escrever(ctx, linha, x, y, t.tracking);
    }
  });

  ctx.restore();
}

function desenharPrimitiva(
  ctx: CanvasRenderingContext2D,
  p: Primitiva,
  imagens: Imagens
): void {
  switch (p.tipo) {
    case "retangulo":
      ctx.fillStyle = p.cor;
      ctx.fillRect(p.x, p.y, p.w, p.h);
      return;

    case "gradiente": {
      const g = ctx.createLinearGradient(p.x, p.y, p.x, p.y + p.h);
      g.addColorStop(0, p.de);
      g.addColorStop(1, p.para);
      ctx.fillStyle = g;
      ctx.fillRect(p.x, p.y, p.w, p.h);
      return;
    }

    case "foto": {
      const img = imagens[p.slot];
      if (img && img.complete && img.naturalWidth > 0)
        cover(ctx, img, p.x, p.y, p.w, p.h);
      else placeholder(ctx, p.rotulo, p.x, p.y, p.w, p.h);
      return;
    }

    case "linha":
      ctx.save();
      ctx.strokeStyle = p.cor;
      ctx.lineWidth = p.largura;
      ctx.beginPath();
      ctx.moveTo(p.x1, p.y1);
      ctx.lineTo(p.x2, p.y2);
      ctx.stroke();
      ctx.restore();
      return;

    case "pilula":
      ctx.fillStyle = p.cor;
      ctx.beginPath();
      ctx.roundRect(p.x, p.y, p.w, p.h, p.raio);
      ctx.fill();
      return;

    case "logo":
      if (p.origem === "vetor") {
        // Coordenadas originais do traço, calibradas à mão. A caixa existe
        // para o PNG; o vetor mantém a posição que sempre teve.
        desenharLogo(ctx, MARGEM, 160, 1.0625);
      } else if (imagens.logo) {
        desenharLogoPng(ctx, imagens.logo, p.x, p.y, p.w, p.h);
      }
      return;

    case "texto":
      desenharTexto(ctx, p);
      return;
  }
}

export interface OpcoesDesenho {
  /**
   * Desenha as linhas da zona segura. SÓ no preview.
   *
   * Elas nunca podem entrar na exportação — seriam publicadas junto com a
   * arte, e o erro só apareceria no perfil do cliente.
   */
  guias?: boolean;
}

export function desenhar(
  ctx: CanvasRenderingContext2D,
  plano: Plano,
  imagens: Imagens = {},
  opts: OpcoesDesenho = {}
): void {
  ctx.clearRect(0, 0, LARGURA, ALTURA);
  for (const p of plano.primitivas) desenharPrimitiva(ctx, p, imagens);

  if (opts.guias) {
    ctx.save();
    ctx.strokeStyle = "rgba(167,139,250,0.85)";
    ctx.lineWidth = 3;
    ctx.setLineDash([18, 14]);
    for (const y of [SEGURO_TOPO, SEGURO_BASE]) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(LARGURA, y);
      ctx.stroke();
    }
    ctx.restore();
  }
}

/**
 * As fontes têm de estar carregadas ANTES do primeiro desenho.
 *
 * Não é detalhe de acabamento: `measureText` com a fonte de fallback devolve
 * métrica errada, e a palavra em destaque do manifesto sai fora de lugar. O
 * sintoma é uma arte torta sem nenhum erro no console.
 */
export async function carregarFontes(): Promise<void> {
  await Promise.all([
    document.fonts.load(`700 88px ${FONTE.sans}`),
    document.fonts.load(`400 30px ${FONTE.sans}`),
    document.fonts.load(`700 170px ${FONTE.mono}`),
    document.fonts.load(`400 24px ${FONTE.mono}`)
  ]);
  await document.fonts.ready;
}
